import React, { use, useState , useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { useQuery } from "@tanstack/react-query";
import {
  fetchNewOrdersSummary,
  fetchHoldOrdersSummary,
  fetchOldOrdersSummary,
  fetchChangesOrdersSummary,
  fetchDepositRequiredSummary,
  fetchCustomOrdersSummary,
  fetchConfirmedOrdersSummary,
  fetchCanceledOrdersSummary,
  fetchGirabatcomOrdersSummary,
} from "../api/OrderAPIs";
import {
ChevronUp,
ChevronDown,
ShoppingCart,
CheckCircle,
Clock,
XCircle,
PackageCheck,
Archive,
AlertTriangle,
Layers,
Tag,
RotateCcw,
Search,
  PackageSearch,
  UserCircle,
  LogOut,
  Shield,
  UserCog,
  Phone,
} from "lucide-react";
import {images} from "../constants/images";

const SkeletonLoader = ({ type }) => {
  if (type === "card") {
    return (
      <div className="animate-pulse bg-white/10 backdrop-blur-sm shadow-xl rounded-2xl p-6 border border-white/10">
        <div className="h-7 bg-white/20 rounded w-3/5 mb-4"></div>
        <div className="space-y-3">
          <div className="h-5 bg-white/15 rounded w-4/5"></div>
          <div className="h-5 bg-white/15 rounded w-3/4"></div>
        </div>
      </div>
    );
  }
  return null;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUnderPurchaseOpen, setIsUnderPurchaseOpen] = useState(false);
  const [isOthersOpen, setIsOthersOpen] = useState(false);
  const [isAdminToolsOpen, setIsAdminToolsOpen] = useState(false);

  // ── Queries ────────────────────────────────────────
  const { data: newOrdersSummary, isLoading: isLoadingNew } = useQuery({
    queryKey: ["newOrdersSummary"],
    queryFn: fetchNewOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: holdOrdersSummary, isLoading: isLoadingHold } = useQuery({
    queryKey: ["holdOrdersSummary"],
    queryFn: fetchHoldOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: changesOrdersSummary, isLoading: isLoadingChanges } = useQuery({
    queryKey: ["changesOrdersSummary"],
    queryFn: fetchChangesOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: depositRequiredSummary, isLoading: isLoadingDeposit } = useQuery({
    queryKey: ["depositRequiredSummary"],
    queryFn: fetchDepositRequiredSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: customOrdersSummary, isLoading: isLoadingCustom } = useQuery({
    queryKey: ["customOrdersSummary"],
    queryFn: fetchCustomOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: canceledOrdersSummary, isLoading: isLoadingCanceled } = useQuery({
    queryKey: ["canceledOrdersSummary"],
    queryFn: fetchCanceledOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: confirmedOrdersSummary, isLoading: isLoadingConfirmed } = useQuery({
    queryKey: ["confirmedOrdersSummary"],
    queryFn: fetchConfirmedOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const { data: girabatcomOrdersSummary, isLoading: isLoadingGirabatcom } = useQuery({
    queryKey: ["girabatcomOrdersSummary"],
    queryFn: fetchGirabatcomOrdersSummary,
    enabled: authState.role === "Admin" || authState.role === "Moderator",
    staleTime: Infinity,
    placeholderData: { count: 0, totalAmount: 0 },
  });

  const isLoading = isLoadingNew || isLoadingHold || isLoadingChanges || isLoadingDeposit || isLoadingCustom;
  const error = null;

  React.useEffect(() => {   
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
    }
  }, [authState, navigate]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const calculateGrandTotals = () => {
    const summaries = [confirmedOrdersSummary, changesOrdersSummary, depositRequiredSummary, customOrdersSummary];
    let totalOrders = 0;
    let totalAmount = 0;

    summaries.forEach((s) => {
      if (s) {
        totalOrders += Number(s.count || 0);
        totalAmount += Number(s.totalAmount || 0);
      }
    });

    return { totalOrders, totalAmount: Math.round(totalAmount) };
  };

  const { totalOrders, totalAmount } = calculateGrandTotals();

  // دالة لعرض اسم الدور بالعربية
  const getRoleName = (role) => {
    switch(role) {
      case "Admin":
        return "مدير النظام";
      case "Moderator":
        return "مشرف";
      default:
        return role;
    }
  };

  // دالة للحصول على أيقونة الدور
  const getRoleIcon = (role) => {
    switch(role) {
      case "Admin":
        return <Shield size={18} className="text-purple-600" />;
      case "Moderator":
        return <UserCog size={18} className="text-blue-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto space-y-10">

        {/* Header – مع إضافة عرض الدور وإصلاح مشكلة القائمة */}
        <div className="relative flex justify-between items-center bg-white/80 backdrop-blur-md shadow-xl rounded-2xl p-4 md:p-6 border border-white/30 z-50">
          <div className="flex items-center gap-4">
            <img
              src={images.logo}          
              alt="Logo"
              className="h-10 object-cover"
            />
            
            {/* عرض الدور هنا */}
            {(authState.role === "Admin" || authState.role === "Moderator") && (
              <div className="hidden md:flex items-center gap-2 bg-gradient-to-r from-indigo-50 to-purple-50 px-4 py-2 rounded-xl border border-indigo-200">
                {getRoleIcon(authState.role)}
                <span className="text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {getRoleName(authState.role)}
                </span>
              </div>
            )}
          </div>

          {/* Mini Menu - مع إصلاح مشكلة الظهور */}
          <div className="relative" style={{ zIndex: 9999 }}>
            <div className="flex items-center gap-3">
              {/* عرض الدور على الموبايل كـ badge صغير */}
              {(authState.role === "Admin" || authState.role === "Moderator") && (
                <div className="md:hidden flex items-center gap-1 bg-gradient-to-r from-indigo-50 to-purple-50 px-2 py-1 rounded-lg border border-indigo-200">
                  {getRoleIcon(authState.role)}
                  <span className="text-xs font-bold text-indigo-600">
                    {authState.role === "Admin" ? "مدير" : "مشرف"}
                  </span>
                </div>
              )}

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2.5 p-1.5 rounded-full hover:bg-white/40 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 relative"
              >
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                  <UserCircle size={28} className="text-white" />
                </div>
              </button>
            </div>

            {/* القائمة المنسدلة - تم تعديل الموقع والـ z-index */}
            {isMenuOpen && (
              <>
                {/* طبقة خلفية شفافة لتقاطعات النقر */}
                <div 
                  className="fixed inset-0" 
                  style={{ zIndex: 9998 }}
                  onClick={() => setIsMenuOpen(false)}
                />

                {/* القائمة نفسها - ظهر فوق كل المحتوى */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 top-14 mt-2 w-56 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-200/60 py-2 overflow-hidden"
                  style={{ zIndex: 9999 }}
                >
                  {/* عرض الدور في القائمة المنسدلة */}
                  <div className="px-5 py-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      {getRoleIcon(authState.role)}
                      <span className="font-medium text-gray-700">
                        {getRoleName(authState.role)}
                      </span>
                    </div>
                    
                  </div>

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-5 py-3 text-center text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={18} />
                    <span className="font-medium">تسجيل الخروج</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* باقي الكود كما هو */}
        {/* Global Summary */}
        {authState.role === "Admin" && (
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white rounded-3xl shadow-2xl p-5 md:p-10 border border-white/20 backdrop-blur-sm ">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 text-center md:text-center">
              إجمالي الطلبات الحالية
            </h2>

            {isLoading ? (
              <div className="flex flex-col sm:flex-row justify-center gap-8 animate-pulse">
                <div className="bg-white/20 rounded-2xl p-8 w-72 text-center">
                  <div className="h-16 w-32 bg-white/30 rounded-xl mx-auto mb-4"></div>
                  <div className="h-6 w-40 bg-white/20 rounded mx-auto"></div>
                </div>
                <div className="bg-white/20 rounded-2xl p-8 w-72 text-center">
                  <div className="h-16 w-48 bg-white/30 rounded-xl mx-auto mb-4"></div>
                  <div className="h-6 w-52 bg-white/20 rounded mx-auto"></div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
                <div className="bg-white/15 backdrop-blur-lg rounded-2xl p-1 text-center border border-white/20 shadow-inner hover:scale-105 transition-transform duration-300">
                  <p className="text-3xl md:text-4xl font-extrabold mb-1">{totalOrders}</p>
                  <p className="text-xl opacity-90 font-medium">عدد الطلبات</p>
                </div>
                <div className="bg-white/15 backdrop-blur-lg rounded-2xl p-1 text-center border border-white/20 shadow-inner hover:scale-105 transition-transform duration-300">
                  <p className="text-3xl md:text-4xl font-extrabold mb-1">
                    {totalAmount.toLocaleString()}
                  </p>
                  <p className="text-xl opacity-90 font-medium">إجمالي المبلغ (ج.م)</p>
                </div>
              </div>
            )}
          </div>
        )}

        

        {/* باقي الكود كما هو من هنا */}
        {/* Summary Cards */}
        {authState.role === "Admin" && (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
              {
                title: "الطلبات الجديدة",
                data: newOrdersSummary,
                color: "from-blue-600 to-cyan-500",
                path: "/new-orders",
              },
              {
                title: "الطلبات المؤكدة",
                data: confirmedOrdersSummary,
                color: "from-green-600 to-green-400",
                path: "/confirmed-orders",
              },
              {
                title: "Girabatcom",
                data: girabatcomOrdersSummary,
                color: "from-cyan-600 to-blue-500",
                path: "/girabatcom-orders",
              },
              {
                title: "الطلبات الملغاة",
                data: canceledOrdersSummary,
                color: "from-red-600 to-rose-500",
                path: "/canceled-orders",
              },
              {
                title: "التصاميم المخصصة",
                data: customOrdersSummary,
                color: "from-teal-700 to-emerald-400",
                path: "/custom-orders",
              },
              {
                title: "الطلبات المعلقة",
                data: holdOrdersSummary,
                color: "from-yellow-600 to-amber-400",
                path: "/hold-orders",
              },
              {
                title: "تحت التغييرات",
                data: changesOrdersSummary,
                color: "from-purple-600 to-violet-400",
                path: "/changes-orders",
              },
              {
                title: "تتطلب دفعة أولية",
                data: depositRequiredSummary,
                color: "from-orange-600 to-orange-400",
                path: "/deposit-required-orders",
              },
            ].map((item, i) => (
              <button
                key={i}
                onClick={() => navigate(item.path)}
                className={`
                  ${i === 0 ? "col-span-1" : ""}
                  bg-gradient-to-br ${item.color} text-white
                  rounded-2xl p-3 md:p-1.5 shadow-lg
                  hover:shadow-2xl hover:scale-[1.03]
                  active:scale-[0.98]
                  transition-all duration-300
                  border border-white/20
                  text-center
                  flex flex-col justify-between
                `}
              >
                <h3 className="text-lg md:text-2xl font-bold">{item.title}</h3>

                {isLoading ? (
                  <SkeletonLoader type="card" />
                ) : (
                  <div className="text-center">
                    <div className="flex items-end gap-2 justify-center">
                      <p className="text-4xl md:text-5xl font-extrabold">
                        {item.data?.count ?? 0}
                      </p>
                      <span className="text-lg md:text-xl opacity-90">طلب</span>
                    </div>

                    <div className="pt-3 border-t border-white/20">
                      <p className="text-lg md:text-xl opacity-90">إجمالي المبلغ</p>
                      <p className="text-2xl md:text-3xl font-bold">
                        {item.data?.totalAmount?.toLocaleString() ?? 0} ج.م
                      </p>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {authState.role === "Moderator" && (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
              {
                title: "الطلبات الجديدة",
                data: newOrdersSummary,
                color: "from-blue-500 to-cyan-500",
                path: "/new-orders",
              },
              {
                title: "الطلبات المؤكدة",
                data: confirmedOrdersSummary,
                color: "from-emerald-500 to-teal-500",
                path: "/confirmed-orders",
              },
              {
                title: "Girabatcom",
                data: girabatcomOrdersSummary,
                color: "from-cyan-600 to-blue-500",
                path: "/girabatcom-orders",
              },
              {
                title: "الطلبات الملغاة",
                data: canceledOrdersSummary,
                color: "from-red-600 to-rose-500",
                path: "/canceled-orders",
              },
              {
                title: "الطلبات المعلقة",
                data: holdOrdersSummary,
                color: "from-yellow-600 to-amber-400",
                path: "/hold-orders",
              },
              {
                title: "تتطلب دفعة أولية",
                data: depositRequiredSummary,
                color: "from-orange-600 to-orange-400",
                path: "/deposit-required-orders",
              },
              {
                title: "تحت التغييرات",
                data: changesOrdersSummary,
                color: "from-purple-600 to-violet-400",
                path: "/changes-orders",
              },
              {
                title: "التصاميم المخصصة",
                data: customOrdersSummary,
                color: "from-teal-500 to-emerald-500",
                path: "/custom-orders",
              },
            ].map((item, i) => (
              <button
                key={i}
                onClick={() => navigate(item.path)}
                className={`
                  bg-gradient-to-br ${item.color} text-white
                  rounded-2xl p-5 md:p-5 shadow-lg
                  hover:shadow-2xl hover:scale-[1.03]
                  active:scale-[0.98]
                  transition-all duration-300
                  border border-white/20
                  text-center
                  flex flex-col justify-between
                `}
              >
                <h3 className="text-lg md:text-2xl font-bold">{item.title}</h3>

                {/* {isLoading ? (
                  <SkeletonLoader type="card" />
                ) : (
                  <div className="text-center">
                    <div className="flex items-end gap-2 justify-center">
                      <p className="text-4xl md:text-5xl font-extrabold">
                        {item.data?.count ?? 0}
                      </p>
                      <span className="text-lg md:text-xl opacity-90">طلب</span>
                    </div>

                    <div className="pt-3 border-t border-white/20">
                      <p className="text-lg md:text-xl opacity-90">إجمالي المبلغ</p>
                      <p className="text-2xl md:text-3xl font-bold">
                        {item.data?.totalAmount?.toLocaleString() ?? 0} ج.م
                      </p>
                    </div>
                  </div>
                )} */}
              </button>
            ))}
            
          </div>
        )}

        {/* ── البحث ── */}
        <div className="space-y-6 border-t border-gray-200 pt-3">
          
          <h3 className="text-4xl font-bold text-center bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
            البحث
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-3xl md:max-w-6xl mx-auto">
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8"> */}


            {/* Search Orders */}
            <button
              onClick={() => navigate("/search-orders")}
              className="
                group bg-white border border-gray-200
                rounded-2xl p-4 md:p-5 shadow-md
                hover:shadow-xl hover:-translate-y-1
                transition-all duration-300
                flex items-center gap-4 md:gap-6 text-right
                w-full
              "
            >
              <div className="bg-indigo-100 text-indigo-600 p-3 md:p-3 rounded-xl group-hover:scale-110 transition">
                <Search size={28} className="md:w-8 md:h-8" />
              </div>

              <div>
                <p className="text-2xl md:text-3xl font-bold text-gray-800">
                  البحث عن طلب
                </p>
                <p className="text-md md:text-xl text-gray-500 mt-1 md:mt-2">
                  ابحث باستخدام رقم الطلب أو بيانات العميل
                </p>
              </div>

            </button>

            {/* Search Designs */}
            <button
              onClick={() => navigate("/search-designs")}
              className="
                group bg-white border border-gray-200
                rounded-2xl p-4 md:p-5 shadow-md
                hover:shadow-xl hover:-translate-y-1
                transition-all duration-300
                flex items-center gap-4 md:gap-6 text-right
                w-full
              "
            >
              <div className="bg-green-100 text-green-600 p-3 md:p-3 rounded-xl group-hover:scale-110 transition">
                <PackageSearch size={28} className="md:w-8 md:h-8" />
              </div>

              <div>
                <p className="text-2xl md:text-3xl font-bold text-gray-800">
                  البحث عن تصميم
                </p>
                <p className="text-md md:text-xl text-gray-500 mt-1 md:mt-2">
                  ابحث عن التصميمات المضافة للنظام
                </p>
              </div>

            </button>

          </div>
        </div>

      {/* === جه ننفذ؟ === */}
      {authState.role === "Admin" && (
      <div className="space-y-6 border-t border-gray-200 pt-3">
        <h3 className="text-4xl font-bold text-center bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">جه ننفذ؟</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      
          {/* WhatsApp Follow-up */}
          <button
            onClick={() => navigate("/follow-up-phones")}
            className="group bg-white border border-gray-200 rounded-2xl p-4 md:p-2 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 md:gap-6 text-right w-full md:col-span-2"
          >
            <div className="bg-green-100 text-green-600 p-4 md:p-6 rounded-xl group-hover:scale-110 transition shrink-0">
              <Phone size={28} className="md:w-6 md:h-6"/>
            </div>

            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-gray-800">متابعة تأكيد WhatsApp</p>
              <p className="text-md md:text-xl text-gray-500 mt-1 md:mt-2">
                إرسال قالب المتابعة (1221) لآخر طلب لكل رقم
              </p>
            </div>
          </button>

          <button
          onClick={() => navigate("/waiting-confirming-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-yellow-100 text-yellow-600 p-4 rounded-xl group-hover:scale-110 transition">
          <Clock size={28}/>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-800">انتظار التأكيد</p>
            <p className="text-md text-gray-500 mt-1">المنتج توفر وجاري التأكيد</p>
          </div>

        </button>

        <button
          onClick={() => navigate("/confirming-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-green-100 text-green-600 p-4 rounded-xl group-hover:scale-110 transition">
          <CheckCircle size={28}/>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-800">نفذ الأوردر</p>
            <p className="text-md text-gray-500 mt-1">نفذ الأوردر عند teeplus</p>
          </div>

        </button>

        <button
          onClick={() => navigate("/cancelled-by-customer-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-red-100 text-red-600 p-4 rounded-xl group-hover:scale-110 transition">
          <XCircle size={28}/>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-800">هيلغي</p>
            <p className="text-md text-gray-500 mt-1">الغي الأوردر عند teeplus</p>
          </div>

        </button>


        </div>
      </div>
      )}
        {/* === أدوات الإدارة === */}
        {authState.role === "Admin" && (
          <div className="mb-10 pd-10 border-t border-gray-200 pt-3">

            <button
              onClick={() => setIsAdminToolsOpen(!isAdminToolsOpen)}
              className="w-full flex justify-center items-center gap-2 text-2xl md:text-3xl font-bold mb-6 text-gray-700 text-purple-600 transition"
            >
              <Layers size={28} className="md:w-6 md:h-6"/>
              أدوات الإدارة
              {isAdminToolsOpen ? <ChevronUp size={24} className="md:w-6 md:h-6"/> : <ChevronDown size={24} className="md:w-6 md:h-6"/>}
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ${
                isAdminToolsOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">

                {/* Categories */}
                <button
                  onClick={() => navigate("/categories")}
                  className="group bg-white border border-gray-200 rounded-2xl p-4 md:p-2 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 md:gap-6 text-right w-full"
                >
                  <div className="bg-purple-100 text-purple-600 p-4 md:p-6 rounded-xl group-hover:scale-110 transition shrink-0">
                    <Tag size={28} className="md:w-6 md:h-6"/>
                  </div>

                  <div className="text-right">
                    <p className="text-xl md:text-3xl font-bold text-gray-800">فئات التصاميم</p>
                    <p className="text-md md:text-xl text-gray-500 mt-1 md:mt-2">
                      إدارة فئات المنتجات
                    </p>
                  </div>
                </button>

                {/* Brands */}
                <button
                  onClick={() => navigate("/brands")}
                  className="group bg-white border border-gray-200 rounded-2xl p-4 md:p-2 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 md:gap-6 text-right w-full"
                >
                  <div className="bg-teal-100 text-teal-600 p-4 md:p-6 rounded-xl group-hover:scale-110 transition shrink-0">
                    <Layers size={28} className="md:w-6 md:h-6"/>
                  </div>

                  <div className="text-right">
                    <p className="text-xl md:text-3xl font-bold text-gray-800">أنواع الهواتف</p>
                    <p className="text-md md:text-xl text-gray-500 mt-1 md:mt-2">
                      إدارة أنواع المنتجات
                    </p>
                  </div>
                </button>
              </div>

            </div>
          </div>
        )}

        {/* === تحت الشراء === */}
        {/* <div className="border-t border-gray-200 pt-3">

          <button
          onClick={() => setIsUnderPurchaseOpen(!isUnderPurchaseOpen)}
          className="w-full flex justify-center items-center gap-2 text-2xl font-bold mb-6 text-indigo-600 transition-colors hover:text-indigo-800"
          >
          <ShoppingCart size={28}/>
          تحت الشراء
          {isUnderPurchaseOpen ? <ChevronUp size={24}/> : <ChevronDown size={24}/>}
          </button>

          <div
          className={`overflow-hidden transition-all duration-300 ${
          isUnderPurchaseOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          }`}
          >

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          <button
          onClick={() => navigate("/under-purchase-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-indigo-100 text-indigo-600 p-4 rounded-xl group-hover:scale-110 transition">
          <ShoppingCart size={28}/>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-800">الطلبات تحت الشراء</p>
            <p className="text-md text-gray-500 mt-1">الطلبات الجاري توفيرها</p>
          </div>

          </button>

          <button
          onClick={() => navigate("/waiting-confirming-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-yellow-100 text-yellow-600 p-4 rounded-xl group-hover:scale-110 transition">
          <Clock size={28}/>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-800">انتظار التأكيد</p>
            <p className="text-md text-gray-500 mt-1">المنتج توفر وجاري التأكيد</p>
          </div>

          </button>

          <button
          onClick={() => navigate("/confirming-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-green-100 text-green-600 p-4 rounded-xl group-hover:scale-110 transition">
          <CheckCircle size={28}/>
          </div>

          <div>
          <p className="text-xl font-bold text-gray-800">تم التأكيد</p>
          <p className="text-md text-gray-500 mt-1">الطلب جاهز للتنفيذ</p>
          </div>

          </button>
          

          </div>
          </div>
        </div> */}

        {/* === أخرى === */}
        <div className="border-t border-gray-200 pt-3">


          <button
          onClick={() => setIsOthersOpen(!isOthersOpen)}
          className="w-full flex justify-center items-center gap-2 text-2xl font-bold mb-6 text-blue-600 transition-colors hover:text-blue-800"
          >
          <Archive size={28}/>
          أخرى
          {isOthersOpen ? <ChevronUp size={24}/> : <ChevronDown size={24}/>}
          </button>

          <div
          className={`overflow-hidden transition-all duration-300 ${
          isOthersOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
          }`}
          >

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          <button
          onClick={() => navigate("/old-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-gray-100 text-gray-600 p-4 rounded-xl group-hover:scale-110 transition">
          <Archive size={28}/>
          </div>

          <div>
          <p className="text-xl font-bold text-gray-800">الطلبات القديمة</p>
          <p className="text-md text-gray-500 mt-1">الطلبات المكتملة سابقاً</p>
          </div>

          </button>

          <button
          onClick={() => navigate("/spam-orders")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >

          <div className="bg-red-100 text-red-600 p-4 rounded-xl group-hover:scale-110 transition">
          <AlertTriangle size={28}/>
          </div>

          <div>
          <p className="text-xl font-bold text-gray-800">الطلبات السبام</p>
          <p className="text-md text-gray-500 mt-1">طلبات غير حقيقية</p>
          </div>

          </button>
          {/* Returns */}
          <button
            onClick={() => navigate("/returns")}
          className="group bg-white border border-gray-200 rounded-2xl p-3 shadow-md hover:shadow-xl hover:-translate-y-1 transition flex items-center gap-4 text-right"
          >
          <div className="bg-red-100 text-red-600 p-4 rounded-xl group-hover:scale-110 transition">
              <RotateCcw size={28} />
            </div>

            <div className="text-right">
              <p className="text-xl font-bold text-gray-800">المرتجعات</p>
              <p className="text-md text-gray-500 mt-1 ">
                إدارة طلبات الإرجاع
              </p>
            </div>
          </button>

          </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default AdminDashboard;